import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarClock,
  Users,
  Target,
  BarChart3,
  HeartHandshake,
  BellRing,
  LogOut,
  Sparkles,
  Building2,
  ChevronDown,
  TrendingUp,
  Package,
  Tag,
  Receipt,
  ClipboardList,
  Wallet,
  Lightbulb,
  Settings as SettingsIcon,
  Database,
  Calculator,
  ShieldCheck,
  UserCog,
  Stethoscope,
  History,
  FileSpreadsheet,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api } from "@/lib/api";
import TwoFactorPanel from "@/components/TwoFactorPanel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const NAV_DASH = { to: "/", label: "Dashboard Sales", icon: LayoutDashboard, end: true };
const NAV_CONSOL = { to: "/consolidated", label: "Ringkasan Konsolidasi", icon: Building2 };
const NAV_BOOKINGS = { to: "/bookings", label: "Booking", icon: CalendarClock };
const NAV_PATIENTS = { to: "/patients", label: "Database (Arsip Pasien)", icon: Users };
const NAV_REG = { to: "/registration", label: "Pendaftaran", icon: ClipboardList };
const NAV_INIT = { to: "/initiatives", label: "Report Inisiatif", icon: Target };
const NAV_LOYALTY = { to: "/loyalty", label: "Loyalty", icon: HeartHandshake };
const NAV_NOTIF = { to: "/notifications", label: "Notifikasi", icon: BellRing };
const NAV_FIN = { to: "/finance", label: "Keuangan / P&L", icon: TrendingUp };
const NAV_ACCT = { to: "/accounting", label: "Akuntansi", icon: Calculator };
const NAV_USERS = { to: "/users", label: "Manajemen User", icon: UserCog };
const NAV_INV = { to: "/inventory", label: "Inventory", icon: Package };
const NAV_PROMOS = { to: "/promos", label: "Promo", icon: Tag };
const NAV_TX = { to: "/transactions", label: "Transaksi POS", icon: Receipt };
const NAV_TXHIST = { to: "/transaction-history", label: "Riwayat Transaksi", icon: History };
const NAV_REPORTS = { to: "/reports", label: "Report", icon: FileSpreadsheet };
const NAV_MR = { to: "/medical-records", label: "Rekam Medis", icon: ClipboardList };
const NAV_COMM = { to: "/commissions", label: "Komisi", icon: Wallet };
const NAV_PA = { to: "/promo-analytics", label: "Promo Analytics", icon: Lightbulb };
const NAV_SETTINGS = { to: "/settings", label: "Pengaturan Clinic", icon: SettingsIcon };
const NAV_MIG = { to: "/migration", label: "Data Migration", icon: Database };
const NAV_ADMIN = { to: "/admin", label: "Konsol Super Admin", icon: ShieldCheck };
const NAV_CONSULT = { to: "/consultation", label: "Konsultasi", icon: Stethoscope };
const NAV_LEADS = { to: "/leads", label: "Input Leads", icon: Target };
const NAV_PERF = { to: "/performance", label: "Laporan Performa", icon: BarChart3 };
const NAV_SALES_REVIEW = { to: "/sales-review", label: "Sales Review", icon: TrendingUp };

const NAV_BY_ROLE = {
  super_admin: [NAV_ADMIN, NAV_DASH, NAV_CONSOL, NAV_BOOKINGS, NAV_CONSULT, NAV_REG, NAV_PATIENTS, NAV_TX, NAV_TXHIST, NAV_REPORTS, NAV_PERF, NAV_SALES_REVIEW, NAV_LEADS, NAV_INIT, NAV_LOYALTY, NAV_NOTIF, NAV_FIN, NAV_ACCT, NAV_MR, NAV_COMM, NAV_INV, NAV_PROMOS, NAV_PA, NAV_USERS, NAV_SETTINGS, NAV_MIG],
  owner: [NAV_DASH, NAV_CONSOL, NAV_BOOKINGS, NAV_CONSULT, NAV_REG, NAV_PATIENTS, NAV_TX, NAV_TXHIST, NAV_REPORTS, NAV_PERF, NAV_SALES_REVIEW, NAV_INIT, NAV_LOYALTY, NAV_NOTIF, NAV_FIN, NAV_ACCT, NAV_COMM, NAV_INV, NAV_PROMOS, NAV_PA, NAV_USERS, NAV_SETTINGS, NAV_MIG],
  manager: [NAV_DASH, NAV_BOOKINGS, NAV_CONSULT, NAV_REG, NAV_PATIENTS, NAV_TX, NAV_TXHIST, NAV_REPORTS, NAV_PERF, NAV_SALES_REVIEW, NAV_LEADS, NAV_INIT, NAV_LOYALTY, NAV_NOTIF, NAV_COMM, NAV_INV, NAV_PROMOS, NAV_PA, NAV_USERS, NAV_SETTINGS, NAV_MIG],
  fo_cs: [NAV_BOOKINGS, NAV_CONSULT, NAV_REG, NAV_PATIENTS, NAV_TX, NAV_TXHIST, NAV_REPORTS, NAV_INV, NAV_PROMOS, NAV_NOTIF, NAV_LOYALTY, NAV_MIG],
  cs: [NAV_BOOKINGS, NAV_LEADS, NAV_PERF, NAV_PATIENTS, NAV_NOTIF],
  crm: [NAV_BOOKINGS, NAV_PERF, NAV_SALES_REVIEW, NAV_PATIENTS, NAV_NOTIF],
  doctor: [NAV_BOOKINGS, NAV_CONSULT, NAV_REG, NAV_PATIENTS, NAV_MR, NAV_COMM, NAV_NOTIF],
  therapist: [NAV_BOOKINGS, NAV_PATIENTS, NAV_INV, NAV_COMM, NAV_NOTIF],
};

const ROLE_LABEL = {
  super_admin: "Super Admin",
  owner: "Owner",
  manager: "Operations Manager",
  fo_cs: "Front Office / CS",
  cs: "Customer Service (CS)",
  crm: "CRM",
  doctor: "Dokter",
  therapist: "Therapist",
};

const ROLE_BADGE = {
  super_admin: "bg-red-100 text-red-800",
  owner: "bg-amber-100 text-amber-800",
  manager: "bg-indigo-100 text-indigo-800",
  fo_cs: "bg-sky-100 text-sky-800",
  cs: "bg-emerald-100 text-emerald-800",
  crm: "bg-yellow-100 text-yellow-800",
  doctor: "bg-emerald-100 text-emerald-800",
  therapist: "bg-pink-100 text-pink-800",
};

export default function Layout() {
  const { user, logout, refreshUser } = useAuth();
  const { branches, branchId, setBranchId } = useBranch();
  const { settings } = useSettings();
  // Branch-unrestricted roles (owner/cs/crm/super_admin) always get the branch switcher,
  // even when the clinic runs in single_branch mode.
  const isBranchUnrestricted = ["owner", "super_admin", "cs", "crm"].includes(user?.role);
  const isSingleMode = settings?.operation_mode === "single_branch" && !isBranchUnrestricted;
  const navigate = useNavigate();

  const isSuper = user?.role === "super_admin";
  const [clinics, setClinics] = useState([]);
  const activeClinic = localStorage.getItem("myclinic_active_clinic") || "";
  useEffect(() => {
    if (isSuper) api.get("/admin/clinics").then((r) => setClinics(r.data)).catch(() => {});
  }, [isSuper]);
  const switchClinic = (cid) => {
    if (cid) localStorage.setItem("myclinic_active_clinic", cid);
    else localStorage.removeItem("myclinic_active_clinic");
    window.location.reload();
  };

  // Multi-clinic group switcher (owner/cs/crm granted access to several clinics)
  const [myClinics, setMyClinics] = useState([]);
  useEffect(() => {
    if (!isSuper && user) api.get("/my-clinics").then((r) => setMyClinics(r.data)).catch(() => {});
  }, [isSuper, user]);
  const isMultiClinic = !isSuper && myClinics.length > 1;
  const currentClinicName = myClinics.find((c) => c.id === user?.clinic_id)?.name || myClinics[0]?.name || "Pilih Klinik";
  const switchMyClinic = (cid) => {
    if (cid && cid === user?.clinic_id) return;
    localStorage.setItem("myclinic_active_clinic", cid);
    localStorage.removeItem("myclinic_active_branch"); // branches differ per clinic
    window.location.reload();
  };

  // Mandatory 2FA enforcement (owner / super_admin) — block app until enrolled
  if (user?.mfa_setup_required) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="force-2fa-gate">
        <div className="w-full max-w-lg space-y-4">
          <div className="text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-foreground text-background">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h1 className="font-heading text-2xl tracking-tight">Aktifkan 2FA untuk Melanjutkan</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Demi keamanan, role Anda wajib mengaktifkan autentikasi dua faktor sebelum mengakses MyClinic.
            </p>
          </div>
          <TwoFactorPanel mandatory onEnrolled={refreshUser} />
          <button onClick={() => { logout(); navigate("/login"); }} className="block w-full text-center text-xs text-muted-foreground hover:text-foreground" data-testid="force-2fa-logout">
            Keluar
          </button>
        </div>
      </div>
    );
  }

  const navItems = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.fo_cs;
  const activeClinicName = clinics.find((c) => c.id === activeClinic)?.name || "Clinic Default";
  const allowedBranchIds = user?.branch_ids || [];
  const visibleBranches = (isBranchUnrestricted || allowedBranchIds.length === 0)
    ? branches
    : branches.filter((b) => allowedBranchIds.includes(b.id));
  const allBranchesLabel = isBranchUnrestricted ? "Semua Cabang" : "Semua Cabang Saya";
  const currentBranchName =
    branchId === "all"
      ? allBranchesLabel
      : branches.find((b) => b.id === branchId)?.name || "Pilih Cabang";

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border bg-card md:flex md:flex-col">
        <div className="flex items-center gap-2 px-6 py-5 border-b border-border">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-foreground text-background">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-heading text-base tracking-tight">MyClinic</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Beauty OS
            </div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to.replace(/\//g, "") || "dashboard"}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border px-3 py-3">
          <div className="rounded-md bg-secondary/60 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Login sebagai
              </div>
              <span className={cn("rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase", ROLE_BADGE[user?.role])}>
                {user?.role}
              </span>
            </div>
            <div className="mt-1 truncate text-sm font-medium">{user?.name}</div>
            <div className="text-xs text-muted-foreground">
              {ROLE_LABEL[user?.role] || user?.role}
              {user?.is_coordinator && " • Koordinator"}
            </div>
          </div>
        </div>
      </aside>

      <div className="md:pl-64">
        <header className="sticky top-0 z-20 border-b border-border bg-card/90 backdrop-blur">
          <div className="flex h-16 items-center justify-between gap-3 px-4 md:px-8">
            <div className="flex items-center gap-3">
              {isSuper && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="clinic-switcher"
                    className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    {activeClinicName}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[240px]">
                    <DropdownMenuLabel>Pilih Clinic (Super Admin)</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="clinic-default" onClick={() => switchClinic("")}>
                      Clinic Default
                    </DropdownMenuItem>
                    {clinics.filter((c) => c.slug !== "default").map((c) => (
                      <DropdownMenuItem key={c.id} data-testid={`clinic-${c.id}`} onClick={() => switchClinic(c.id)}>
                        {c.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {isMultiClinic && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="my-clinic-switcher"
                    className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    <Building2 className="h-4 w-4" />
                    {currentClinicName}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[240px]">
                    <DropdownMenuLabel>Pilih Klinik</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {myClinics.map((c) => (
                      <DropdownMenuItem key={c.id} data-testid={`my-clinic-${c.id}`} onClick={() => switchMyClinic(c.id)}>
                        {c.name}
                        {c.id === user?.clinic_id && <span className="ml-auto text-[10px] text-emerald-600">aktif</span>}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {isSingleMode ? (
                <div className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-sm font-medium" data-testid="branch-single">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  {currentBranchName}
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">single</span>
                </div>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="branch-switcher"
                    className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-secondary"
                  >
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {currentBranchName}
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[220px]">
                    <DropdownMenuLabel>Pilih Cabang</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="branch-all" onClick={() => setBranchId("all")}>
                      {allBranchesLabel}
                    </DropdownMenuItem>
                    {visibleBranches.map((b) => (
                      <DropdownMenuItem key={b.id} data-testid={`branch-${b.id}`} onClick={() => setBranchId(b.id)}>
                        {b.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu"
                className="inline-flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-sm hover:bg-secondary"
              >
                <div className="grid h-7 w-7 place-items-center rounded-full bg-foreground text-background text-xs font-medium">
                  {(user?.name || "U").slice(0, 1).toUpperCase()}
                </div>
                <div className="hidden text-left md:block">
                  <div className="text-sm font-medium leading-tight">{user?.name}</div>
                  <div className="text-[10px] text-muted-foreground">{ROLE_LABEL[user?.role]}</div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="nav-security"
                  onClick={() => navigate("/security")}
                >
                  <ShieldCheck className="mr-2 h-4 w-4" /> Keamanan (2FA)
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate("/login"); }}
                >
                  <LogOut className="mr-2 h-4 w-4" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6 md:py-8 pb-24 md:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation (IMP 6) */}
      <nav
        data-testid="mobile-bottom-nav"
        className="fixed bottom-0 inset-x-0 z-40 flex md:hidden border-t border-border bg-card/95 backdrop-blur"
      >
        {navItems.slice(0, 5).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`bnav-${n.to.replace(/\//g, "") || "dashboard"}`}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium",
                isActive ? "text-emerald-700" : "text-muted-foreground"
              )
            }
          >
            <n.icon className="h-5 w-5" />
            <span className="truncate max-w-[64px]">{n.label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
